import { lazy, Suspense } from 'react';
import { useT } from '@/i18n/use-t';

/**
 * Monaco is ~3 MB. Lazy-load it so the receipt-config editor only
 * adds the chunk for admins who navigate here — the rest of the
 * admin bundle stays slim.
 */
const Editor = lazy(() => import('@monaco-editor/react').then((m) => ({ default: m.default })));

export type MonacoYamlEditorProps = {
  value: string;
  onChange: (v: string) => void;
  height?: number | string;
  readOnly?: boolean;
};

export function MonacoYamlEditor({
  value,
  onChange,
  height = 500,
  readOnly = false,
}: MonacoYamlEditorProps) {
  const t = useT('admin_ui');
  return (
    <Suspense
      fallback={
        <div
          className="flex items-center justify-center rounded-md border border-border bg-card text-sm text-muted-foreground"
          style={{ height }}
        >
          {t('receiptConfig.loadingEditor', 'Loading editor…')}
        </div>
      }
    >
      <div className="overflow-hidden rounded-md border border-border">
        <Editor
          height={typeof height === 'number' ? `${height}px` : height}
          defaultLanguage="yaml"
          value={value}
          theme="vs-dark"
          options={{
            readOnly,
            minimap: { enabled: false },
            fontSize: 13,
            tabSize: 2,
            wordWrap: 'on',
            scrollBeyondLastLine: false,
          }}
          onChange={(v) => onChange(v ?? '')}
        />
      </div>
    </Suspense>
  );
}
